import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cashApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';

/**
 * This terminal's drawer (cash-off, 18 Sep 2026): whether it has one, the open session
 * with its live expected cash, the venue's cash settings, the safe balance. One read of
 * `GET /cash/drawer` when someone signs in, refreshed after every cash write and every
 * cash payment, so the header word and the Till page never disagree with the server.
 *
 * A terminal with no drawer (`VERLO_CASH_DRAWER=0` — the floor tills at Yard & Coop) has
 * `hasDrawer` false: the payment dialog hides Cash and the header shows no Till.
 */
const DrawerContext = createContext({ drawer: null, loaded: false, hasDrawer: true, session: null, refresh: () => {} });

export const DrawerProvider = ({ children }) => {
  const { user } = useAuth();
  const [drawer, setDrawer] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => cashApi.drawer()
    .then(({ data }) => { setDrawer(data); setLoaded(true); return data; })
    .catch(() => { setLoaded(true); return null; }), []);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  const value = useMemo(() => ({
    drawer,
    loaded,
    hasDrawer: drawer ? !!drawer.has_drawer : true,
    session: drawer?.session || null,
    settings: drawer?.settings || null,
    canManage: !!drawer?.can_manage,
    refresh,
  }), [drawer, loaded, refresh]);

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
};

export const useDrawer = () => useContext(DrawerContext);

export default DrawerContext;
